import { format } from "date-fns/format";
import { subMonths } from "date-fns/subMonths";
import { subYears } from "date-fns/subYears";
import ReactECharts, { EChartsOption } from "echarts-for-react";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { DisplayGroup } from "@/models/displayGroup";
import { VocCountData } from "@/models/vocCount";

function getBarChartOptions(
  latestYM: Date,
  stats: number[],
  chartTitle: string,
  t: TFunction,
  isYearlyView: boolean = false,
): EChartsOption {
  const getPastYearMonths = (baseDate: Date) => {
    return Array.from({ length: 12 }, (_, i) => {
      const pastDate = subMonths(baseDate, i);
      return pastDate.getMonth() === 0
        ? format(pastDate, t("bi.chart.monthFormat.yearMonth"))
        : format(pastDate, t("bi.chart.monthFormat.month"));
    }).toReversed();
  };

  const getPastYears = (baseDate: Date) => {
    return Array.from({ length: 5 }, (_, i) => {
      const pastDate = subYears(baseDate, i);
      return format(pastDate, "yyyy");
    }).toReversed();
  };

  return {
    title: {
      show: true,
      text: chartTitle,
      left: "50%",
      padding: [1, 0, 0, -10],
      textAlign: "center",
      textStyle: { fontWeight: "bold", color: "#000", fontSize: "13px" },
    },
    grid: {
      top: 40,
      bottom: 20,
      right: 10,
    },
    xAxis: {
      type: "category",
      data: isYearlyView ? getPastYears(latestYM) : getPastYearMonths(latestYM),
    },
    yAxis: { type: "value" },
    series: [
      {
        data: stats,
        type: "bar",
        itemStyle: {
          color: "#57AEFE",
        },
      },
    ],
    tooltip: {
      trigger: "axis",
    },
  };
}

interface Props {
  vocCountData: VocCountData;
  selectedCategoryPath: string;
  displayGroup: DisplayGroup;
  currentStats: number[];
  isYearlyView?: boolean;
}

const BarChart: React.FC<Props> = ({
  vocCountData,
  selectedCategoryPath,
  displayGroup,
  currentStats,
  isYearlyView = false,
}) => {
  const { t } = useTranslation("voiceek_ui");

  const title = selectedCategoryPath === "/" ? displayGroup.name : selectedCategoryPath.split("/").slice(-2)[0];
  const options = getBarChartOptions(
    vocCountData.latestYearMonth,
    currentStats,
    t("bi.chart.vocTrend", { displayGroupOrCategory: title }),
    t,
    isYearlyView,
  );

  return (
    <div className="py-4 w-[550px] border border-gray-200 rounded-[6px]">
      <ReactECharts
        option={options}
        style={{ height: "146px", width: "516px" }}
      />
    </div>
  );
};

export default BarChart;
